import { translate } from '@jsverse/transloco';
import * as Highcharts from 'highcharts';
import { Subject } from 'rxjs';

import { Utils } from 'app/utils/utils';
import { PlotType } from '../../../components/widget-predictive/widget-predictive.component';
import { Breakdown, Driver } from '../../../models/predictive.models';
import { HighchartsDirector, HighchartsDirectorInputs } from '../highcharts.director';
import { CONSTANTS } from './quad-chart.constants';

export interface QuadChartDirectorInputs extends HighchartsDirectorInputs {
  breakdown: Breakdown;
}

export interface QuadChartPointCustomData extends Highcharts.Dictionary<any> {
  breakdown: string;
  driver: string;
  field: string;
}

export interface QuadChartDirectorOptions {
  plotType: PlotType;
  decimals: number;
  displayLabels: boolean;
  showNumericAxisLabels: boolean;
  pointClickSubject: Subject<Highcharts.PointClickEventObject>;
}

export class QuadChartDirector extends HighchartsDirector<QuadChartDirectorInputs> {
  private readonly elements: Highcharts.SVGElement[] = [];

  constructor(private readonly options: QuadChartDirectorOptions) {
    super();
  }

  construct(inputs: QuadChartDirectorInputs): Highcharts.Options {
    return this.builder
      .withChart(this.getChart(this.options.plotType))
      .withSeries(
        QuadChartDirector.getSeries(
          inputs.breakdown,
          this.options.decimals,
          this.options.displayLabels
        )
      )
      .withXAxis(QuadChartDirector.getXAxis(this.options.showNumericAxisLabels))
      .withYAxis(QuadChartDirector.getYAxis(this.options.showNumericAxisLabels))
      .withLegend({ enabled: false })
      .withTooltip(QuadChartDirector.getTooltip(this.options.displayLabels))
      .withPlotOptions(QuadChartDirector.getPlotOptions(this.options.pointClickSubject))
      .build();
  }

  private getChart(plotType: PlotType): Highcharts.ChartOptions {
    const that = this;

    return {
      type: CONSTANTS.CHART.TYPE,
      events: {
        render() {
          that.handleRender(this, plotType);
        },
      },
    };
  }

  private handleRender(chart: Highcharts.Chart, plotType: PlotType): void {
    QuadChartDirector.fixScatterSeriesRendering(chart);

    this.destroyElements();

    this.drawElements(chart, plotType);
  }

  private static fixScatterSeriesRendering(chart: Highcharts.Chart): void {
    // https://www.highcharts.com/forum/viewtopic.php?t=49872

    chart.series.forEach((s) => {
      const line = s.graph;
      const path = line.element.attributes.getNamedItem('d').value;
      const pathArray = path.split(' ');
      pathArray[1] = (+pathArray[1] + 0.0001).toString();
      line.attr({ d: pathArray.join(' ') });
    });
  }

  private destroyElements(): void {
    this.elements.forEach((el) => el.destroy());
    this.elements.splice(0, this.elements.length);
  }

  private drawElements(chart: Highcharts.Chart, plotType: PlotType): void {
    const { xPosition, yPosition } = QuadChartDirector.calculateReferenceLines(chart, plotType);
    this.drawQuadrantArea(chart, xPosition, yPosition);
    this.drawQuadrantLines(chart, xPosition, yPosition);
    this.drawQuadrantLabels(chart, xPosition, yPosition);
  }

  private static calculateReferenceLines(
    chart: Highcharts.Chart,
    plotType: PlotType
  ): { xPosition: number; yPosition: number } {
    const yValues: number[] = [];
    const xValues: number[] = [];

    chart.series.forEach((serie) => {
      const point = serie.data[0];
      yValues.push(point.y);
      xValues.push(point.x);
    });

    const yPosition = plotType === 'Average' ? Utils.average(yValues) : Utils.median(yValues);
    const xPosition = plotType === 'Average' ? Utils.average(xValues) : Utils.median(xValues);

    return {
      xPosition: QuadChartDirector.mapRangeToRange(
        xPosition,
        chart.xAxis[0].min,
        chart.xAxis[0].max,
        chart.plotLeft,
        chart.plotWidth + chart.plotLeft
      ),
      yPosition: QuadChartDirector.mapRangeToRange(
        yPosition,
        chart.yAxis[0].min,
        chart.yAxis[0].max,
        chart.plotTop,
        chart.plotHeight + chart.plotTop,
        true
      ),
    };
  }

  private drawQuadrantLines(chart: Highcharts.Chart, xPosition: number, yPosition: number) {
    this.elements.push(
      chart.renderer
        .rect(xPosition, chart.plotTop, 1, chart.plotHeight)
        .attr({ fill: CONSTANTS.DRAWING.PLOT_LINE_COLOR })
        .add()
    );

    this.elements.push(
      chart.renderer
        .rect(chart.plotLeft, yPosition, chart.plotWidth, 1)
        .attr({ fill: CONSTANTS.DRAWING.PLOT_LINE_COLOR })
        .add()
    );
  }

  private drawQuadrantArea(chart: Highcharts.Chart, xPosition: number, yPosition: number) {
    const boxAreaWidth = chart.plotWidth - xPosition + chart.plotLeft;

    this.elements.push(
      chart.renderer
        .rect(xPosition, yPosition, boxAreaWidth, chart.plotTop + chart.plotHeight - yPosition)
        .attr({ fill: CONSTANTS.DRAWING.FOCUS_AREA_COLOR })
        .add()
    );
  }

  private drawQuadrantLabels(chart: Highcharts.Chart, xPosition: number, yPosition: number) {
    const boxAreaWidth = chart.plotWidth - xPosition + chart.plotLeft;
    const centerX = xPosition + boxAreaWidth / 2;

    const mBoxAreaHeight = yPosition - chart.plotTop;
    const mCenterY = chart.plotTop + mBoxAreaHeight / 2;

    const fBoxAreaHeight = chart.plotTop + chart.plotHeight - yPosition + 1;
    const fCenterY = chart.plotTop + yPosition + fBoxAreaHeight / 2;

    this.elements.push(
      chart.renderer
        .text(translate('widgets.charting.maintain'), centerX, mCenterY)
        .css({
          color: CONSTANTS.DRAWING.MAINTAIN_TEXT_COLOR,
          fontSize: CONSTANTS.DRAWING.FONT_SIZE,
          fontWeight: CONSTANTS.DRAWING.FONT_WEIGHT,
        })
        .attr({ align: CONSTANTS.DRAWING.TEXT_ALIGN })
        .add()
    );

    this.elements.push(
      chart.renderer
        .text(translate('widgets.charting.focus'), centerX, fCenterY)
        .css({
          color: CONSTANTS.DRAWING.FOCUS_TEXT_COLOR,
          fontSize: CONSTANTS.DRAWING.FONT_SIZE,
          fontWeight: CONSTANTS.DRAWING.FONT_WEIGHT,
        })
        .attr({ align: CONSTANTS.DRAWING.TEXT_ALIGN })
        .add()
    );
  }

  private static getXAxis(showNumericAxisLabels: boolean): Highcharts.XAxisOptions {
    let xAxis: Highcharts.XAxisOptions = {
      title: {
        text: translate('widgets.charting.importance'),
      },
      minPadding: CONSTANTS.AXIS.PADDING_X,
      maxPadding: CONSTANTS.AXIS.PADDING_X,
      tickLength: CONSTANTS.AXIS.TICK_LENGHT_DEFAULT_X,
      startOnTick: CONSTANTS.AXIS.START_ON_TICK,
      endOnTick: CONSTANTS.AXIS.END_ON_TICK,
      labels: QuadChartDirector.getAxisLabel(showNumericAxisLabels),
      lineColor: CONSTANTS.AXIS.GRID_LINE_COLOR,
    };

    if (showNumericAxisLabels) {
      xAxis = {
        ...xAxis,
        tickColor: CONSTANTS.AXIS.GRID_LINE_COLOR,
        tickLength: CONSTANTS.AXIS.TICK_LENGHT_X,
        tickInterval: CONSTANTS.AXIS.TICK_INTERVAL_X,
        startOnTick: CONSTANTS.AXIS.START_ON_TICK_X,
        endOnTick: CONSTANTS.AXIS.END_ON_TICK_X,
      };
    }

    return xAxis;
  }

  private static getYAxis(showNumericAxisLabels: boolean): Highcharts.YAxisOptions {
    return {
      title: {
        text: translate('widgets.charting.performance'),
      },
      startOnTick: CONSTANTS.AXIS.START_ON_TICK,
      endOnTick: CONSTANTS.AXIS.END_ON_TICK,
      labels: QuadChartDirector.getAxisLabel(showNumericAxisLabels),
      tickAmount: CONSTANTS.AXIS.TICK_AMOUNT_Y,
      gridLineColor: CONSTANTS.AXIS.GRID_LINE_COLOR,
    };
  }

  private static getAxisLabel(showNumericAxisLabels: boolean): {
    formatter: Highcharts.AxisLabelsFormatterCallbackFunction;
    style: Highcharts.CSSObject;
  } {
    return {
      formatter() {
        if (showNumericAxisLabels) {
          return (
            isNaN(Number(this.value)) ? this.value : (this.value as number).toFixed(2)
          ) as string;
        }

        if (this.isFirst) {
          return translate('widgets.charting.low');
        }

        if (this.isLast) {
          return translate('widgets.charting.high');
        }

        return '';
      },
      style: {
        fontSize: CONSTANTS.AXIS.LABEL_FONT_SIZE,
        fontWeight: CONSTANTS.AXIS.LABEL_FONT_WEIGHT,
        color: CONSTANTS.COLOR.BLUE,
      },
    };
  }

  private static getTooltip(displayLabels: boolean): Highcharts.TooltipOptions {
    const that = this;

    return {
      formatter() {
        return that.pointFormatter(this, displayLabels);
      },
    };
  }

  private static pointFormatter(point: Highcharts.Point, displayLabels: boolean): string {
    const pointCustomData = point.options.custom as QuadChartPointCustomData;
    return displayLabels
      ? translate('widgets.charting.tooltip.score-part', { score: point.y })
      : `<b>${pointCustomData.driver}</b>`;
  }

  private static pointLabelFormatter(
    point: Highcharts.Point,
    displayLabels: boolean
  ): string | number {
    const pointCustomData = point.options.custom as QuadChartPointCustomData;
    return displayLabels ? Utils.truncate(pointCustomData.driver) : point.y;
  }

  private static getSeries(
    breakdown: Breakdown,
    decimals: number,
    displayLabels: boolean
  ): Highcharts.SeriesScatterOptions[] {
    const series: Highcharts.SeriesScatterOptions[] = [];

    breakdown.drivers.forEach((d) => {
      const seriesData: Highcharts.PointOptionsObject[] = [];
      let seriesColor: Highcharts.GradientColorObject;

      const x = Utils.round(d.importancePopulator, 0);
      const y = Utils.round(d.current, decimals);

      seriesData.push(QuadChartDirector.createPoint(x, y, d, breakdown.name));

      if (d.simulated != null) {
        const s = Utils.round(d.simulated, decimals);

        seriesData.push(QuadChartDirector.createPoint(x, s, d, breakdown.name));

        seriesColor = this.createSeriesColor(d.current, d.simulated);
      }

      series.push(this.createSeries(d.name, seriesData, seriesColor, displayLabels));
    });

    return series;
  }

  private static createPoint(
    x: number,
    y: number,
    driver: Driver,
    breakdown: string
  ): Highcharts.PointOptionsObject {
    const custom: QuadChartPointCustomData = {
      breakdown,
      driver: driver.fieldLongName,
      field: driver.name,
    };

    return {
      x,
      y,
      custom,
    };
  }

  private static createSeries(
    name: string,
    data: Highcharts.PointOptionsObject[],
    color: Highcharts.GradientColorObject,
    displayLabels: boolean
  ): Highcharts.SeriesScatterOptions {
    return {
      type: 'scatter',
      name,
      data,
      color,
      dataLabels: this.createDataLabels(displayLabels),
    };
  }

  private static createDataLabels(
    displayLabels: boolean
  ): Highcharts.PlotScatterDataLabelsOptions[] {
    const that = this;
    const dataLabels: Highcharts.PlotScatterDataLabelsOptions[] = [
      {
        verticalAlign: 'middle',
        allowOverlap: CONSTANTS.DATA_LABEL.OVERLAP,
        style: {
          color: CONSTANTS.DATA_LABEL.POINT.TEXT_COLOR,
          fontWeight: CONSTANTS.DATA_LABEL.POINT.FONT_WEIGHT,
          textOutline: displayLabels
            ? CONSTANTS.DATA_LABEL.TEXT_OUTLINE.BLUE
            : CONSTANTS.DATA_LABEL.TEXT_OUTLINE.NONE,
        },
        formatter() {
          return that.pointLabelFormatter(this, displayLabels);
        },
      },
    ];

    return dataLabels;
  }

  private static createSeriesColor(
    current: number,
    simulated: number
  ): Highcharts.GradientColorObject {
    const startColor = simulated > current ? CONSTANTS.COLOR.BLUE : CONSTANTS.COLOR.GRAY;
    const endColor = simulated > current ? CONSTANTS.COLOR.GRAY : CONSTANTS.COLOR.BLUE;

    return {
      linearGradient: CONSTANTS.GRADIENT.SERIES_LINEAR,
      stops: [
        [CONSTANTS.GRADIENT.COLOR_STOP_1, startColor],
        [CONSTANTS.GRADIENT.COLOR_STOP_2, startColor],
        [CONSTANTS.GRADIENT.COLOR_STOP_3, endColor],
        [CONSTANTS.GRADIENT.COLOR_STOP_4, endColor],
      ],
    };
  }

  private static getPlotOptions(
    pointClickSubject: Subject<Highcharts.PointClickEventObject>
  ): Highcharts.PlotOptions {
    return {
      series: {
        point: {
          events: {
            click(event) {
              pointClickSubject.next(event);
            },
          },
        },
      },
      scatter: {
        lineWidth: CONSTANTS.CHART.SERIES_LINE_WIDTH,
        dataLabels: {
          enabled: CONSTANTS.DATA_LABEL.ENABLED,
        },
        marker: {
          enabled: CONSTANTS.MARKER.ENABLED,
          radius: CONSTANTS.MARKER.RADIUS,
          symbol: CONSTANTS.MARKER.SYMBOL,
          fillColor: CONSTANTS.COLOR.BLUE,
        },
      },
    };
  }

  private static mapRangeToRange(
    value: number,
    inputStart: number,
    inputEnd: number,
    outputStart: number,
    outputEnd: number,
    mirrored = false
  ): number {
    const slope = (outputEnd - outputStart) / (inputEnd - inputStart);
    if (mirrored) {
      return outputEnd - slope * (value - inputStart);
    }

    return outputStart + slope * (value - inputStart);
  }
}
